import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

import {
  platformBillingApi,
  platformBillingQueryKeys,
  type ReconciliationFinding,
} from "../../shared/api/platformBilling";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const VND = new Intl.NumberFormat("vi-VN");

/**
 * Bỏ phần lẻ giây trong dấu thời gian Postgres nằm giữa câu mô tả.
 *
 * <p>Câu mô tả do SQL ghép, nên nó mang nguyên {@code 2026-09-15 21:39:05.741164+07}. Sáu chữ số
 * micro giây không nói thêm điều gì cho người đang đọc để quyết định, chỉ làm dòng chữ khó đọc.
 * Sửa ở đây thay vì sửa SQL: hàm đối soát đã chạy trên dev, đổi nó cần một migration mới cho một
 * thay đổi thuần hiển thị.
 */
function tidyTimestamps(detail: string): string {
  return detail.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\.\d+/g, "$1");
}


/**
 * Mỗi phép kiểm trả lời một câu hỏi khác nhau về chỗ tiền có thể rơi mất.
 *
 * Nhãn thiếu thì bảng in nguyên mã phép kiểm — xấu nhưng không giấu. Một phép kiểm mới không bao
 * giờ được biến mất khỏi màn hình chỉ vì chưa ai đặt tên tiếng Việt cho nó.
 */
const CHECK_LABELS: Record<string, string> = {
  LEDGER_VS_BALANCE: "Sổ cái và số dư lệch nhau",
  PAID_NO_CREDIT: "Đã nhận tiền, ví chưa được cộng",
  CREDIT_NO_INTENT: "Ví được cộng mà không có đơn",
  STALE_PENDING: "Đơn treo quá lâu",
  CHARGE_NO_PERIOD: "Đã trừ tiền mà không có kỳ",
  PROPAGATION_STUCK: "Quyền lợi chưa lan truyền được",
  PRICE_MISMATCH: "Thu khác giá đã thoả thuận",
  UNMATCHED_TRANSFER: "Tiền về nhưng không biết của ai",
  UPGRADE_PAID_NOT_APPLIED: "Đã trả tiền nâng gói mà gói chưa đổi",
  UPGRADE_AMOUNT_MISMATCH: "Thu khác báo giá nâng gói",
  INVOICE_STALE: "Hoá đơn GTGT chưa xuất quá lâu",
  INVOICE_SUBMITTED_STUCK: "Đã gửi nhà cung cấp, chưa có số hoá đơn",
  INVOICE_NO_REFERENCE: "Hoá đơn đã phát hành nhưng không tra lại được",
  DUPLICATE_TRANSFER: "Một mã đơn nhận hai lần chuyển khoản",
  CREDIT_AMOUNT_MISMATCH: "Ví cộng khác số ngân hàng báo",
  INVOICE_RETRY_HIGH: "Hoá đơn thử mãi không xong",
  INVOICE_PROVIDER_DOWN: "Nhiều hoá đơn hỏng cùng một lý do",
  INVOICE_UNCERTAIN: "Không rõ PA đã tạo hoá đơn hay chưa",
  INVOICE_BACKLOG: "Quy mô hàng đợi hoá đơn",
};

/**
 * Đối soát tiền SaaS, cho quản trị viên nền tảng (#18990 P2.3).
 *
 * <p>Trang này **không có nút sửa**, và đó là chủ ý. Sổ cái là sự thật; một chỗ lệch là câu hỏi
 * cho một người, không phải việc để máy tự quyết. Muốn chỉnh số dư thì đi qua đường điều chỉnh ví
 * — có lý do bắt buộc và ghi audit, và lần điều chỉnh đó hiện trên chính sao kê phòng khám đọc.
 *
 * <p>Mỗi dòng nêu **người nhận** và **hành động**. Một bản in ra toàn số sẽ được đọc đúng một lần
 * rồi thôi, vì người đọc không biết dòng trước mặt có phải việc của mình không.
 */
export function PlatformReconciliationPage() {
  const q = useQuery({
    queryKey: platformBillingQueryKeys.reconciliation(),
    queryFn: () => platformBillingApi.reconciliation(),
  });

  if (q.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const findings = q.data?.findings ?? [];
  const critical = findings.filter((f) => f.severity === "CRITICAL");
  const warnings = findings.filter((f) => f.severity !== "CRITICAL");

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground">Đối soát tiền SaaS</h1>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            Bảy phép kiểm trên tiền phòng khám trả cho Addy. Trang này <b>chỉ báo, không tự sửa</b>:
            sổ cái là sự thật, còn một chỗ lệch là câu hỏi cho một người.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => q.refetch()} disabled={q.isFetching}>
          <RefreshCw className={q.isFetching ? "mr-1 h-3.5 w-3.5 animate-spin" : "mr-1 h-3.5 w-3.5"} />
          Kiểm lại
        </Button>
      </div>

      {q.isError && <p className="text-sm text-destructive">{apiError(q.error).detail}</p>}

      {findings.length === 0 && !q.isError && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/5 p-3 text-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          <span>
            <b>Mọi con số khớp nhau.</b> Không có đơn nào đã thu mà chưa vào ví, không có ví nào
            lệch sổ, không có kỳ nào thiếu dòng trừ tiền.
          </span>
        </div>
      )}

      {critical.length > 0 && (
        <Section
          title={`${critical.length} vấn đề NGHIÊM TRỌNG — có tiền đang treo`}
          tone="critical"
          findings={critical}
        />
      )}
      {warnings.length > 0 && (
        <Section
          title={`${warnings.length} mục cần kiểm tra`}
          tone="warning"
          findings={warnings}
        />
      )}
    </div>
  );
}

function Section({
  title,
  tone,
  findings,
}: {
  title: string;
  tone: "critical" | "warning";
  findings: ReconciliationFinding[];
}) {
  return (
    <div className="space-y-2">
      <h2
        className={
          tone === "critical"
            ? "flex items-center gap-1.5 text-sm font-semibold text-destructive"
            : "flex items-center gap-1.5 text-sm font-semibold text-amber-700"
        }
      >
        <AlertTriangle className="h-4 w-4" />
        {title}
      </h2>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[56rem] table-fixed text-sm">
          {/* Hai bảng xếp chồng nhau, nên chúng phải cùng một khung cột — nếu để mỗi bảng tự
              co giãn theo nội dung của mình thì các cột lệch nhau và mắt phải đọc lại từ đầu. */}
          <colgroup>
            <col className="w-[34%]" />
            <col className="w-[10%]" />
            <col className="w-[11%]" />
            <col className="w-[13%]" />
            <col className="w-[32%]" />
          </colgroup>
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Phép kiểm</th>
              <th className="px-3 py-2 text-left font-medium">Phòng khám</th>
              <th className="px-3 py-2 text-right font-medium">Số tiền</th>
              <th className="px-3 py-2 text-left font-medium">Ai xử lý</th>
              <th className="px-3 py-2 text-left font-medium">Làm gì</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f, i) => (
              <tr key={`${f.check}-${f.tenantId}-${i}`} className="border-t align-top">
                <td className="px-3 py-2">
                  <div className="font-medium text-foreground">
                    {CHECK_LABELS[f.check] ?? f.check}
                  </div>
                  <div className="text-xs text-muted-foreground">{tidyTimestamps(f.detail)}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {f.tenantId ? f.tenantId.slice(0, 8) : "—"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {f.amountVnd == null ? "—" : `${VND.format(f.amountVnd)}đ`}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{f.owner}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{f.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
